import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import Base from './Base';
import Swal from 'sweetalert2';

const AdminDesignerDashboard = () => {
    const { authTokens } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState('designers');
    const [designers, setDesigners] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        designerStatus: '',
        payoutStatus: ''
    });
    const [pagination, setPagination] = useState({
        designers: { currentPage: 1, totalPages: 1, totalCount: 0 },
        payouts: { currentPage: 1, totalPages: 1, totalCount: 0 }
    });

    useEffect(() => {
        if (activeTab === 'designers') {
            fetchDesigners();
        } else {
            fetchPayouts();
        }
    }, [activeTab, filters]);

    const fetchDesigners = async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                page_size: '10'
            });

            if (filters.designerStatus) {
                params.append('status', filters.designerStatus);
            }

            const response = await axios.get(`https://vasstrinfo.com/api/admin/designers/?${params}`, {
                headers: {
                    'Authorization': `Bearer ${authTokens?.access}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setDesigners(response.data.data.designers);
                setPagination(prev => ({
                    ...prev,
                    designers: response.data.data.pagination
                }));
            }
        } catch (error) {
            console.error('Error fetching designers:', error);
            Swal.fire('Error', 'Failed to fetch designers data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchPayouts = async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                page_size: '10'
            });

            if (filters.payoutStatus) {
                params.append('status', filters.payoutStatus);
            }

            const response = await axios.get(`https://vasstrinfo.com/api/admin/payouts/?${params}`, {
                headers: {
                    'Authorization': `Bearer ${authTokens?.access}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setPayouts(response.data.data.payouts);
                setPagination(prev => ({
                    ...prev,
                    payouts: response.data.data.pagination
                }));
            }
        } catch (error) {
            console.error('Error fetching payouts:', error);
            Swal.fire('Error', 'Failed to fetch payouts data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const updateDesignerStatus = async (designerId, newStatus, adminNotes = '') => {
        try {
            const response = await axios.put(
                `https://vasstrinfo.com/api/admin/designers/${designerId}/status/`,
                {
                    onboarding_status: newStatus,
                    admin_notes: adminNotes
                },
                {
                    headers: {
                        'Authorization': `Bearer ${authTokens?.access}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                Swal.fire('Success', 'Designer status updated successfully', 'success');
                fetchDesigners(pagination.designers.currentPage);
            } else {
                Swal.fire('Error', response.data.message || 'Failed to update status', 'error');
            }
        } catch (error) {
            console.error('Error updating designer status:', error);
            Swal.fire('Error', 'Failed to update designer status', 'error');
        }
    };

    const retryPayout = async (payoutId) => {
        try {
            const response = await axios.post(
                `https://vasstrinfo.com/api/admin/payouts/${payoutId}/retry/`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${authTokens?.access}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                Swal.fire('Success', 'Payout retry initiated successfully', 'success');
                fetchPayouts(pagination.payouts.currentPage);
            } else {
                Swal.fire('Error', response.data.message || 'Failed to retry payout', 'error');
            }
        } catch (error) {
            console.error('Error retrying payout:', error);
            Swal.fire('Error', 'Failed to retry payout', 'error');
        }
    };

    const handleStatusChange = (designer) => {
        Swal.fire({
            title: 'Update Designer Status',
            html: `
                <div class="text-left">
                    <label class="block text-sm font-medium text-gray-700 mb-2">Status:</label>
                    <select id="status-select" class="w-full p-2 border border-gray-300 rounded mb-4">
                        <option value="pending" ${designer.onboarding_status === 'pending' ? 'selected' : ''}>Pending</option>
                        <option value="under_review" ${designer.onboarding_status === 'under_review' ? 'selected' : ''}>Under Review</option>
                        <option value="approved" ${designer.onboarding_status === 'approved' ? 'selected' : ''}>Approved</option>
                        <option value="rejected" ${designer.onboarding_status === 'rejected' ? 'selected' : ''}>Rejected</option>
                    </select>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Admin Notes:</label>
                    <textarea id="admin-notes" class="w-full p-2 border border-gray-300 rounded" rows="3" placeholder="Add any notes...">${designer.admin_notes || ''}</textarea>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Update',
            preConfirm: () => {
                const status = document.getElementById('status-select').value;
                const notes = document.getElementById('admin-notes').value;
                return { status, notes };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                updateDesignerStatus(designer.id, result.value.status, result.value.notes);
            }
        });
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            'pending': 'bg-gray-100 text-gray-800',
            'under_review': 'bg-yellow-100 text-yellow-800',
            'approved': 'bg-green-100 text-green-800',
            'rejected': 'bg-red-100 text-red-800',
            'success': 'bg-green-100 text-green-800',
            'failed': 'bg-red-100 text-red-800',
            'processing': 'bg-blue-100 text-blue-800'
        };

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
                {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    return (
        <Base Content={
            <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 py-8 px-4">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="mb-8 text-center">
                        <h1 className="text-4xl font-bold text-orange-500 mb-3">Designer Management Dashboard</h1>
                        <p className="text-gray-700 text-lg">Manage designer onboarding and payouts</p>
                    </div>

                    {/* Tabs */}
                    <div className="mb-8">
                        <div className="bg-white rounded-xl shadow-lg border border-orange-200 p-2">
                            <nav className="flex space-x-2">
                                <button
                                    onClick={() => setActiveTab('designers')}
                                    className={`flex-1 py-3 px-6 rounded-lg font-bold text-lg transition-colors duration-200 ${
                                        activeTab === 'designers'
                                            ? 'bg-orange-500 text-white shadow-md'
                                            : 'text-orange-600 hover:bg-orange-50'
                                    }`}
                                >
                                    Designers ({pagination.designers.totalCount})
                                </button>
                                <button
                                    onClick={() => setActiveTab('payouts')}
                                    className={`flex-1 py-3 px-6 rounded-lg font-bold text-lg transition-colors duration-200 ${
                                        activeTab === 'payouts'
                                            ? 'bg-orange-500 text-white shadow-md'
                                            : 'text-orange-600 hover:bg-orange-50'
                                    }`}
                                >
                                    Payouts ({pagination.payouts.totalCount})
                                </button>
                            </nav>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="mb-8 bg-white rounded-xl shadow-lg p-6 border border-orange-200">
                        <h3 className="text-xl font-bold text-orange-600 mb-4">Filters</h3>
                        <div className="flex flex-wrap gap-4">
                            {activeTab === 'designers' ? (
                                <div>
                                    <label className="block text-lg font-bold text-orange-500 mb-2">Status Filter</label>
                                    <select
                                        value={filters.designerStatus}
                                        onChange={(e) => setFilters(prev => ({ ...prev, designerStatus: e.target.value }))}
                                        className="px-4 py-3 border border-orange-300 rounded-lg bg-[#fffaf3] text-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="pending">Pending</option>
                                        <option value="under_review">Under Review</option>
                                        <option value="approved">Approved</option>
                                        <option value="rejected">Rejected</option>
                                    </select>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-lg font-bold text-orange-500 mb-2">Status Filter</label>
                                    <select
                                        value={filters.payoutStatus}
                                        onChange={(e) => setFilters(prev => ({ ...prev, payoutStatus: e.target.value }))}
                                        className="px-4 py-3 border border-orange-300 rounded-lg bg-[#fffaf3] text-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="pending">Pending</option>
                                        <option value="processing">Processing</option>
                                        <option value="success">Success</option>
                                        <option value="failed">Failed</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="bg-white rounded-xl shadow-lg border border-orange-200">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-orange-500"></div>
                                <span className="ml-4 text-orange-600 text-lg font-medium">Loading data...</span>
                            </div>
                        ) : (
                            <div>
                                {activeTab === 'designers' ? (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designer</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Type</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {designers.map((designer) => (
                                                    <tr key={designer.id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div>
                                                                <div className="text-sm font-medium text-gray-900">{designer.name}</div>
                                                                <div className="text-sm text-gray-500">@{designer.username}</div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="text-sm text-gray-900">{designer.email}</div>
                                                            <div className="text-sm text-gray-500">{designer.phone}</div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {designer.business_type.charAt(0).toUpperCase() + designer.business_type.slice(1)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            {getStatusBadge(designer.onboarding_status)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatDate(designer.created_at)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                            <button
                                                                onClick={() => handleStatusChange(designer)}
                                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                            >
                                                                Update Status
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designer</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Amount</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {payouts.map((payout) => (
                                                    <tr key={payout.id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div>
                                                                <div className="text-sm font-medium text-gray-900">{payout.designer_name}</div>
                                                                <div className="text-sm text-gray-500">{payout.designer_email}</div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            #{payout.order_id}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {formatCurrency(payout.amount)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {formatCurrency(payout.commission_amount)} ({payout.commission_percentage}%)
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {formatCurrency(payout.net_amount)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            {getStatusBadge(payout.payout_status)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                            {formatDate(payout.created_at)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                            {payout.payout_status === 'failed' && (
                                                                <button
                                                                    onClick={() => retryPayout(payout.id)}
                                                                    className="text-red-600 hover:text-red-900"
                                                                >
                                                                    Retry
                                                                </button>
                                                            )}
                                                            {payout.failure_reason && (
                                                                <button
                                                                    onClick={() => Swal.fire('Failure Reason', payout.failure_reason, 'info')}
                                                                    className="text-blue-600 hover:text-blue-900 ml-3"
                                                                >
                                                                    View Error
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* Pagination */}
                                {(activeTab === 'designers' ? pagination.designers.totalPages : pagination.payouts.totalPages) > 1 && (
                                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                        <div className="flex-1 flex justify-between sm:hidden">
                                            <button
                                                onClick={() => {
                                                    const currentPage = activeTab === 'designers'
                                                        ? pagination.designers.currentPage
                                                        : pagination.payouts.currentPage;
                                                    if (currentPage > 1) {
                                                        activeTab === 'designers'
                                                            ? fetchDesigners(currentPage - 1)
                                                            : fetchPayouts(currentPage - 1);
                                                    }
                                                }}
                                                disabled={
                                                    (activeTab === 'designers' ? pagination.designers.currentPage : pagination.payouts.currentPage) === 1
                                                }
                                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                            >
                                                Previous
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const currentPage = activeTab === 'designers'
                                                        ? pagination.designers.currentPage
                                                        : pagination.payouts.currentPage;
                                                    const totalPages = activeTab === 'designers'
                                                        ? pagination.designers.totalPages
                                                        : pagination.payouts.totalPages;
                                                    if (currentPage < totalPages) {
                                                        activeTab === 'designers'
                                                            ? fetchDesigners(currentPage + 1)
                                                            : fetchPayouts(currentPage + 1);
                                                    }
                                                }}
                                                disabled={
                                                    (activeTab === 'designers' ? pagination.designers.currentPage : pagination.payouts.currentPage) ===
                                                    (activeTab === 'designers' ? pagination.designers.totalPages : pagination.payouts.totalPages)
                                                }
                                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                            >
                                                Next
                                            </button>
                                        </div>
                                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                            <div>
                                                <p className="text-sm text-gray-700">
                                                    Showing page{' '}
                                                    <span className="font-medium">
                                                        {activeTab === 'designers' ? pagination.designers.currentPage : pagination.payouts.currentPage}
                                                    </span>{' '}
                                                    of{' '}
                                                    <span className="font-medium">
                                                        {activeTab === 'designers' ? pagination.designers.totalPages : pagination.payouts.totalPages}
                                                    </span>
                                                </p>
                                            </div>
                                            <div>
                                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                                    {/* Pagination buttons would go here */}
                                                </nav>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        } />
    );
};

export default AdminDesignerDashboard;